<template>
  <div>
    <v-dialog :value="model.active" width="80vw" persistent>
      <!-- Modal content -->
      <v-card>
        <div class="d-flex justify-space-between align-center pr-2">
          <v-card-title class="text-h6 font-weight-bold"> Document Upload </v-card-title>
          <v-btn icon @click="closeDialog"><v-icon>fa-close</v-icon></v-btn>
        </div>
        <v-divider></v-divider>

        <v-card-text class="pt-5">
          <v-row class="d-flex" style="height: 50vh">
            <v-col cols="8" class="h-100">
              <v-data-table
                :headers="headers"
                :items="documents"
                hide-default-footer
                @click:row="rowClicked"
                @dblclick:row="rowDblClicked">
                <template v-slot:[`item.revisionNumber`]="{ item }">
                  <v-chip small color="primary">{{ item.revisionNumber }}</v-chip>
                </template>

                <template v-slot:[`item.documentTypeId`]="{ item }">
                  <v-chip small>{{ getDocumentTypeDisplay(item.documentTypeId) }}</v-chip>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <v-menu offset-y>
                    <template v-slot:activator="{ on }">
                      <v-btn icon v-on="on">
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item @click="editDocument(item)">
                        <v-list-item-icon>
                          <v-icon>mdi-pencil</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Edit</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="deleteDocument(item)">
                        <v-list-item-icon>
                          <v-icon>mdi-delete</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Delete</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </template>
              </v-data-table>
            </v-col>
            <v-col cols="4" class="h-100 pb-5 d-flex justify-center flex-column">
              <DocumentViewer
                :key="selected?.id"
                :item="selected"
                ref="documentViewer"
                class="h-100 w-100"></DocumentViewer>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            color="primary"
            small
            elevation="0"
            :loading="model.loading"
            @click="submit"
            :disabled="!documents || documents.length == 0 || !model.valid">
            Done
          </v-btn>
          <v-btn elevation="0" small :disabled="!model.hasChanges" @click="discard">
            discard
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="red" small elevation="0" text @click="closeDialog"> close </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <AddDocumentModal ref="uploadDocumentsModal" @uploadCompleted="onUploadCompleted" />

    <UpdateDocumentModal ref="updateDocumentModal" @updateCompleted="onUpdateCompleted" />
  </div>
</template>

<script>
  import AddDocumentModal from '@/components/attachments/add-document-modal.vue';
  import UpdateDocumentModal from '@/components/attachments/update-document-modal.vue';
  import headers from '../config/upload-documents-header';
  import DocumentViewer from './DocumentViewer.vue';
  import { mapGetters } from 'vuex';
  import { $uuidv4 } from '@/utils';

  export default {
    data() {
      return {
        taskId: '',
        options: null,
        documents: [],
        selected: null,
        model: {
          active: false,
          valid: false,
          loading: false,
        },
        headers,
      };
    },
    computed: {
      ...mapGetters('DOCUMENT_TYPE', {
        loading: 'GET_LOADING',
        documentTypes: 'GET_ENTRIES',
      }),
      forgeTags: function () {
        return this.$store.getters.GET_ALL_EXISTING_TAGS;
      },
      assetId: {
        get() {
          return this.$router.currentRoute.params.id;
        },
      },
      defaultTypeName: function () {
        return this.$store.getters['GET_DEFAULT_DOCUMENT_TYPE'] ?? 'Specifications';
      },
      defaultTypeId: function () {
        let docType = this.documentTypes.find((t) => t.name == this.defaultTypeName);
        return docType ? docType.id : null;
      },
    },
    created() {
      this.$store.dispatch('DOCUMENT_TYPE/GET');
    },
    methods: {
      open(taskId, options = null) {
        this.taskId = taskId;
        this.options = options;
        this.$refs.uploadDocumentsModal.open({ singleFile: this.options?.singleFile || false });
      },
      closeDialog() {
        this.$refs.uploadDocumentsModal.closeDialog();
        this.model.active = false;
      },
      discard() {
        this.$refs.uploadDocumentsModal.closeDialog();
        this.model.active = false;
      },
      onUploadCompleted(result) {
        let documents = structuredClone(result);

        documents.forEach((doc) => {
          doc.id = $uuidv4();
          doc._isTemporary = true; // Tells DocumentViewer to use storageReference instead of proxy endpoint
          doc.assetId = this.assetId;
          doc.revisionNumber = '00';
          doc.taskId = this.taskId;
          doc.documentTypeId = this.defaultTypeId;
          if (this.options && this.options.type == 'add-revision') {
            (doc.documentTitle = this.options.document.documentTitle),
              (doc.documentNumber = this.options.document.documentNumber),
              (doc.documentTypeId = this.options.document.documentTypeId),
              (doc.forgeTagsIds = this.options.document.forgeTagsIds);
          } else {
            (doc.documentTitle = doc.fileName),
              (doc.documentNumber = doc.fileName),
              (doc.forgeTagsIds = []);
          }
        });

        this.documents = documents;
        this.selected = this.documents[0];

        this.model.active = true;
      },
      onUpdateCompleted(item) {
        let index = this.documents.findIndex((e) => e.id == item.id);
        this.documents[index] = item;
        this.documents = this.documents.map((e) => e);
      },
      submit() {
        // A second click while saving would upload the files twice.
        if (this.model.loading) return;
        var payLoad = structuredClone(this.documents);
        let request = null;

        if (this.options) {
          switch (this.options.type) {
            case 'add-revision':
              payLoad = structuredClone(this.documents[0]);
              payLoad.documentId = this.options.document.id;

              request = this.$store.dispatch('TASK_DOCUMENT/ADD_REVISION', payLoad).then(() => {
                this.closeDialog();
              });
              break;

            default:
              break;
          }
        } else {
          request = this.$store.dispatch('TASK_DOCUMENT/ADD', payLoad).then((res) => {
            // Cancelled at the same-name prompt: keep the files so the user can change them.
            if (!res?.cancelled) this.closeDialog();
          });
        }
        if (!request) return;
        this.model.loading = true;
        request
          // The store action has already shown the error.
          .catch(() => {})
          .finally(() => {
            this.model.loading = false;
          });
      },
      openDocument(item) {
        this.selected = item;
      },
      rowClicked(mouseEvent, ev) {
        this.selected = ev.item;
        // this.openDocument(ev.item);
      },
      rowDblClicked(mouseEvent, ev) {
        this.editDocument(ev.item);
      },
      editDocument(item) {
        var clone = structuredClone(item);
        this.$refs.updateDocumentModal.open(clone);
      },
      deleteDocument(item) {
        this.documents = this.documents.filter((e) => e != item);
        if (this.documents.length) this.selected = this.documents[0];
        else this.selected = null;
      },
      tagDisplayName(id) {
        return this.forgeTags.find((t) => t.id == id)?.name ?? '';
      },
      getDocumentTypeDisplay(id) {
        return this.documentTypes.find((d) => d.id == id)?.name ?? '';
      },
    },
    watch: {
      documents() {
        for (let i = 0; i < this.documents.length; i++) {
          const doc = this.documents[i];
          var notValid = !doc.documentTitle || !doc.documentNumber || !doc.revisionNumber;
          if (notValid) {
            this.model.valid = false;
            return;
          }
        }

        this.model.valid = true;
      },
    },
    components: {
      AddDocumentModal,
      DocumentViewer,
      UpdateDocumentModal,
    },
  };
</script>

<style></style>
